import type { Artikelstamm, ArtikelstammDelta, StammPruefsumme, Syncstand } from './api';
import type { KassenProfil } from './schnellwahl';

/**
 * Automatische Synchronisierung der Kasse (P8) – app-weit, unabhängig vom geöffneten Reiter.
 *
 * Der Server ist Datenführer. Die Kasse hält einen **vollständigen** Rohstamm (alle Gruppen und
 * Artikel inkl. deaktivierter, mit Änderungscursor) und leitet daraus die Schnellwahl ab.
 *
 *   Erstabgleich        `seit=0` → vollständiger Stand
 *   Delta               nur übernehmen, wenn `seit` = lokaler Cursor; sonst vollständig neu
 *   Übernahme           neuer Rohstamm zuerst dauerhaft ablegen, dann umschalten
 *   Auslöser            Start, Ereignis „aenderung“, online, sichtbar, alle 60 s Syncstand,
 *                       Serverneustart, Meldung „Preis veraltet“ (409), alle 6 h Prüfsumme
 *   Fehler              exponentielles Warten (5 s … 5 min); ungültiger Kassenschlüssel hält
 *                       den Dienst an (keine Fehlversuche am Server), bis er neu angestoßen wird
 *
 * Der Dienst verändert nie den offenen Bon: Positionen behalten ihren Preis; der Server weist
 * einen veralteten Preis vor dem Zahlungsstart ab (S-5) und die Kasse zeigt die Meldung.
 */

export type StammGruppe = Artikelstamm['gruppen'][number];
export type StammArtikel = Artikelstamm['artikel'][number];

/** Vollständiger Rohstamm mit Cursor (Grundlage jedes Deltas). */
export interface RohStamm {
  unternehmerId: string;
  version: number;
  aktualisiertAm: string;
  syncStand: number;
  gruppen: StammGruppe[];
  artikel: StammArtikel[];
  /** Schnellwahl-Profil der Kasse (P10); fehlt/null = bisheriges Verhalten. */
  profil?: KassenProfil | null;
}

export type SyncZustand =
  | { art: 'start' }
  | { art: 'synchron'; am: string }
  | { art: 'aktualisiert'; am: string }
  | { art: 'offline'; lokalerStand: boolean }
  | { art: 'fehler'; grund: string }
  | { art: 'anmeldungUngueltig' };

/** Antwort des Delta-Abrufs (aus Sicht des Dienstes). */
export type DeltaErgebnis =
  { status: 304 } | { status: 200; etag: string | null; daten: ArtikelstammDelta };

export interface SyncQuelle {
  delta(seit: number, etag: string | null): Promise<DeltaErgebnis>;
  syncstand(): Promise<Syncstand>;
  pruefsumme(): Promise<StammPruefsumme>;
  /**
   * Vollabruf ohne Cursor (ältere Server ohne Delta: 501). Nur aktive Zeilen; die Kasse lädt
   * dann alle {@link OHNE_DELTA_MS} vollständig neu.
   */
  voll?(): Promise<Artikelstamm>;
  /** Schnellwahl-Profil der Kasse (P10). Fehlt es, gilt das bisherige Verhalten. */
  profil?(): Promise<KassenProfil | null>;
  /** Ereigniskanal; endet bei Schließen/Abbruch. Fehlt er, genügt die 60-s-Prüfung. */
  ereignisse?(
    beiEreignis: (art: string, daten: unknown) => void,
    signal: AbortSignal,
  ): Promise<void>;
}

export interface RohAblage {
  lies(): Promise<unknown>;
  schreibe(r: RohStamm): Promise<void>;
}

/** Fehler mit HTTP-Status (wie ApiFehler der Kasse). */
interface MitStatus {
  status: number;
}

function hatStatus(e: unknown): e is MitStatus {
  return typeof e === 'object' && e !== null && typeof (e as MitStatus).status === 'number';
}

export const SYNCSTAND_MS = 60_000;
export const PRUEFSUMME_MS = 6 * 60 * 60_000;
export const WARTEN_MIN_MS = 5_000;
export const WARTEN_MAX_MS = 5 * 60_000;
export const OHNE_DELTA_MS = 5 * 60_000;

/** Server ohne Delta-Abgleich – wird wie eine 501-Antwort behandelt. */
function ohneDeltaFehler(): Error {
  return Object.assign(new Error('Server ohne Delta-Abgleich'), { status: 501 });
}

/** Lauf einer gestoppten Dienstgeneration: Ergebnis verwerfen, nichts mehr schreiben. */
class Veraltet extends Error {
  override readonly name = 'Veraltet';
}

/** Rohstamm aus der Ablage prüfen (fremde oder alte Einträge werden verworfen). */
export function alsRohStamm(x: unknown): RohStamm | null {
  if (typeof x !== 'object' || x === null) {
    return null;
  }
  const r = x as Record<string, unknown>;
  if (
    typeof r.unternehmerId !== 'string' ||
    typeof r.version !== 'number' ||
    typeof r.syncStand !== 'number' ||
    typeof r.aktualisiertAm !== 'string' ||
    !Array.isArray(r.gruppen) ||
    !Array.isArray(r.artikel)
  ) {
    return null;
  }
  return {
    unternehmerId: r.unternehmerId,
    version: r.version,
    aktualisiertAm: r.aktualisiertAm,
    syncStand: r.syncStand,
    gruppen: r.gruppen as StammGruppe[],
    artikel: r.artikel as StammArtikel[],
    ...(typeof r.profil === 'object' ? { profil: r.profil as KassenProfil | null } : {}),
  };
}

/**
 * Antwort auf den lokalen Stand anwenden (rein). `null` = Delta passt nicht zum lokalen Cursor
 * (dann vollständig neu laden).
 */
export function wendeAn(lokal: RohStamm | null, d: ArtikelstammDelta): RohStamm | null {
  const kopf = {
    unternehmerId: d.unternehmerId,
    version: d.version,
    aktualisiertAm: d.aktualisiertAm,
    syncStand: d.syncStand,
  };
  const profil = lokal?.profil === undefined ? {} : { profil: lokal.profil };
  if (d.vollstaendig) {
    return { ...kopf, ...profil, gruppen: [...d.gruppen], artikel: [...d.artikel] };
  }
  if (lokal?.syncStand !== d.seit || lokal.unternehmerId !== d.unternehmerId) {
    return null;
  }
  const gruppen = new Map(lokal.gruppen.map((g) => [g.id, g]));
  for (const g of d.gruppen) {
    gruppen.set(g.id, g);
  }
  const artikel = new Map(lokal.artikel.map((a) => [a.id, a]));
  for (const a of d.artikel) {
    artikel.set(a.id, a);
  }
  return { ...kopf, ...profil, gruppen: [...gruppen.values()], artikel: [...artikel.values()] };
}

/** Rohstamm → Form der Kassen-Antwort (Gruppen/Artikel nach Reihenfolge wie der Server). */
export function alsArtikelstamm(r: RohStamm): Artikelstamm {
  return {
    unternehmerId: r.unternehmerId,
    version: r.version,
    aktualisiertAm: r.aktualisiertAm,
    gruppen: r.gruppen,
    artikel: r.artikel,
  };
}

/** Text der Prüfsumme wie am Server: `g:<id>:<stand>;` je Gruppe, dann `a:…` je Artikel, nach ID. */
export function pruefsummenText(r: RohStamm): string {
  const g = [...r.gruppen]
    .sort((x, y) => x.id - y.id)
    .map((x) => `g:${String(x.id)}:${String(x.syncStand ?? 0)};`)
    .join('');
  const a = [...r.artikel]
    .sort((x, y) => x.id - y.id)
    .map((x) => `a:${String(x.id)}:${String(x.syncStand ?? 0)};`)
    .join('');
  return g + a;
}

export async function sha256Hex(text: string): Promise<string> {
  const daten = new TextEncoder().encode(text);
  const hash = await crypto.subtle.digest('SHA-256', daten);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export interface Planer {
  setTimeout(f: () => void, ms: number): unknown;
  clearTimeout(h: unknown): void;
}

const ECHTER_PLANER: Planer = {
  setTimeout: (f, ms) => setTimeout(f, ms),
  clearTimeout: (h) => {
    clearTimeout(h as ReturnType<typeof setTimeout>);
  },
};

export class SyncDienst {
  private roh: RohStamm | null = null;
  private etag: string | null = null;
  private serverstart: string | null = null;
  private letztePruefsumme = 0;
  private fehlerFolge = 0;
  private laeuft: Promise<void> | null = null;
  /** Während eines Laufs angestoßen: danach genau ein weiterer Lauf mit diesem Grund. */
  private naechsterGrund: string | null = null;
  private gestoppt = true;
  private zeitgeber: unknown = null;
  private kanal: AbortController | null = null;
  private kanalWarten = WARTEN_MIN_MS;
  private zustandJetzt: SyncZustand = { art: 'start' };
  /** Erhöht sich mit jedem stoppe(): verspätete Antworten älterer Läufe schreiben nichts. */
  private generation = 0;
  /** Server ohne Delta (501): Vollabruf im Abstand {@link OHNE_DELTA_MS}. */
  private ohneDelta = false;
  /** Syncstand meldet ein anderes Schnellwahl-Profil (P10) – beim nächsten Lauf laden. */
  private profilPruefen = true;
  private readonly hoerer = new Set<(z: SyncZustand) => void>();

  constructor(
    private readonly opt: {
      quelle: SyncQuelle;
      ablage: RohAblage;
      /** Neuer Rohstamm (nach dem dauerhaften Ablegen) – Schnellwahl ableiten und anzeigen. */
      uebernehme: (r: RohStamm) => void;
      jetzt?: () => Date;
      planer?: Planer;
      /** Ist das Gerät online (navigator.onLine)? */
      online?: () => boolean;
      /**
       * Version der angezeigten Schnellwahl. Ist sie älter als der Rohstamm (z. B. weil eine
       * andere Quelle dazwischen schrieb), wird sie neu abgeleitet.
       */
      anzeigeVersion?: () => number | null;
    },
  ) {}

  get zustand(): SyncZustand {
    return this.zustandJetzt;
  }

  /** Aktueller Rohstamm (nur lesen). */
  get stamm(): RohStamm | null {
    return this.roh;
  }

  abonniere(f: (z: SyncZustand) => void): () => void {
    this.hoerer.add(f);
    f(this.zustandJetzt);
    return () => {
      this.hoerer.delete(f);
    };
  }

  private setze(z: SyncZustand): void {
    this.zustandJetzt = z;
    for (const f of this.hoerer) {
      f(z);
    }
  }

  private jetzt(): Date {
    return this.opt.jetzt?.() ?? new Date();
  }

  private get planer(): Planer {
    return this.opt.planer ?? ECHTER_PLANER;
  }

  /** Start: lokalen Stand laden, abgleichen, Prüfungen planen, Ereigniskanal öffnen. */
  async starte(): Promise<void> {
    this.gestoppt = false;
    const gen = this.generation;
    if (this.roh === null) {
      let gelesen: RohStamm | null;
      try {
        gelesen = alsRohStamm(await this.opt.ablage.lies());
      } catch {
        gelesen = null;
      }
      if (gen !== this.generation) {
        return; // inzwischen gestoppt
      }
      // Nur übernehmen, wenn nicht schon ein (neuerer) Stand im Speicher liegt
      if (gelesen !== null && this.stamm === null) {
        this.roh = gelesen;
        this.opt.uebernehme(gelesen);
      }
    }
    await this.anstossen('start');
    this.oeffneKanal();
  }

  stoppe(): void {
    this.gestoppt = true;
    this.generation++;
    this.laeuft = null;
    this.naechsterGrund = null;
    if (this.zeitgeber !== null) {
      this.planer.clearTimeout(this.zeitgeber);
      this.zeitgeber = null;
    }
    this.kanal?.abort();
    this.kanal = null;
  }

  /**
   * Abgleich anstoßen (Ereignis, online, sichtbar, 409 „Preis veraltet“ …). Läuft schon einer,
   * folgt genau ein weiterer – nie parallel.
   */
  anstossen(grund: string): Promise<void> {
    if (this.gestoppt) {
      return Promise.resolve();
    }
    if (grund === 'anmeldung' && this.zustandJetzt.art === 'anmeldungUngueltig') {
      this.fehlerFolge = 0;
    }
    if (this.laeuft !== null) {
      // Ein gezielter Anlass (Ereignis, 409 …) geht dem bloßen Zeitgeber vor
      if (this.naechsterGrund === null || grund !== 'syncstand') {
        this.naechsterGrund = grund;
      }
      return this.laeuft;
    }
    const gen = this.generation;
    const lauf = (async () => {
      let g: string | null = grund;
      while (g !== null && !this.gestoppt && gen === this.generation) {
        await this.lauf(g, gen);
        // Gehört der Lauf nicht mehr zur aktuellen Generation, den Grund der neuen nicht verbrauchen
        g = gen === this.generation ? this.nimmNaechstenGrund() : null;
      }
    })().finally(() => {
      if (this.laeuft === lauf) {
        this.laeuft = null;
      }
    });
    this.laeuft = lauf;
    return lauf;
  }

  private nimmNaechstenGrund(): string | null {
    const g = this.naechsterGrund;
    this.naechsterGrund = null;
    return g;
  }

  /** Nach jedem await: gehört der Lauf noch zur aktuellen Generation? */
  private pruefe(gen: number): void {
    if (gen !== this.generation) {
      throw new Veraltet();
    }
  }

  private async lauf(grund: string, gen: number): Promise<void> {
    if (this.opt.online?.() === false) {
      this.setze({ art: 'offline', lokalerStand: this.roh !== null });
      this.plane(SYNCSTAND_MS);
      return;
    }
    try {
      if (this.ohneDelta) {
        const neu = await this.holeVoll(gen);
        this.fehlerFolge = 0;
        this.setze({ art: neu ? 'aktualisiert' : 'synchron', am: this.jetzt().toISOString() });
        this.plane(OHNE_DELTA_MS);
        return;
      }
      const neu = await this.abgleich(grund === 'syncstand' ? 'syncstand' : 'delta', gen);
      // Erst nach dem Abgleich bewerten: ein dabei erkannter Serverneustart macht sie fällig
      const pruefsummeFaellig =
        grund === 'start' ||
        grund === 'serverstart' ||
        this.jetzt().getTime() - this.letztePruefsumme >= PRUEFSUMME_MS;
      if (pruefsummeFaellig) {
        await this.pruefeSumme(gen);
      }
      // P10: Profil bei Start, Hinweis oder gemeldeter Änderung laden
      const profilNeu =
        grund !== 'syncstand' || this.profilPruefen ? await this.pruefeProfil(gen) : false;
      this.pruefe(gen);
      // Anzeige hinter dem Rohstamm (andere Quelle schrieb dazwischen)? Neu ableiten.
      const angezeigt = this.opt.anzeigeVersion?.();
      if (
        this.roh !== null &&
        angezeigt !== undefined &&
        (angezeigt === null || angezeigt < this.roh.version)
      ) {
        this.opt.uebernehme(this.roh);
      }
      this.fehlerFolge = 0;
      this.setze({
        art: neu || profilNeu ? 'aktualisiert' : 'synchron',
        am: this.jetzt().toISOString(),
      });
      this.plane(SYNCSTAND_MS);
    } catch (e: unknown) {
      if (e instanceof Veraltet || gen !== this.generation) {
        return;
      }
      if (
        hatStatus(e) &&
        e.status === 501 &&
        !this.ohneDelta &&
        this.opt.quelle.voll !== undefined
      ) {
        // Server ohne Delta-Abgleich: ausdrücklich auf Vollabruf umstellen
        this.ohneDelta = true;
        await this.lauf(grund, gen);
        return;
      }
      this.fehlerBehandeln(e);
    }
  }

  private fehlerBehandeln(e: unknown): void {
    if (hatStatus(e) && e.status === 401) {
      // Ungültiger Kassenschlüssel: anhalten – jeder weitere Versuch wäre ein Fehlversuch.
      this.setze({ art: 'anmeldungUngueltig' });
      this.kanal?.abort();
      return;
    }
    this.fehlerFolge++;
    const warten = Math.min(WARTEN_MAX_MS, WARTEN_MIN_MS * 2 ** (this.fehlerFolge - 1));
    if (!hatStatus(e) || e.status === 0) {
      this.setze({ art: 'offline', lokalerStand: this.roh !== null });
    } else {
      this.setze({
        art: 'fehler',
        grund: e instanceof Error ? e.message : `HTTP ${String(e.status)}`,
      });
    }
    this.plane(warten);
  }

  private plane(ms: number): void {
    if (this.gestoppt) {
      return;
    }
    if (this.zeitgeber !== null) {
      this.planer.clearTimeout(this.zeitgeber);
    }
    this.zeitgeber = this.planer.setTimeout(() => {
      this.zeitgeber = null;
      void this.anstossen('syncstand');
    }, ms);
  }

  /**
   * Abgleich: bei `syncstand` zuerst den leichten Syncstand prüfen und nur bei Abweichung das
   * Delta holen. Liefert true, wenn ein neuer Stand übernommen wurde.
   */
  private async abgleich(art: 'syncstand' | 'delta', gen: number): Promise<boolean> {
    if (art === 'syncstand' && this.roh !== null) {
      const s = await this.opt.quelle.syncstand().catch((e: unknown) => {
        // 404: Server ohne Syncstand (älterer Stand nach Rückfall) → Vollabruf
        throw hatStatus(e) && e.status === 404 ? ohneDeltaFehler() : e;
      });
      this.pruefe(gen);
      // P10: Profil-Stand vergleichen (fehlt bei älterer API → nicht prüfen)
      if (s.schnellwahl !== undefined) {
        const server =
          s.schnellwahl === null
            ? 'kein'
            : `${String(s.schnellwahl.profilId)}-${String(s.schnellwahl.version)}`;
        const lokal =
          this.roh.profil == null
            ? 'kein'
            : `${String(this.roh.profil.id)}-${String(this.roh.profil.version)}`;
        if (server !== lokal) {
          this.profilPruefen = true;
        }
      }
      const neustart = this.serverstart !== null && s.serverstart !== this.serverstart;
      this.serverstart = s.serverstart;
      if (neustart) {
        this.letztePruefsumme = 0; // nach Serverneustart vollständig prüfen
      }
      if (
        !neustart &&
        s.stamm !== null &&
        s.stamm.syncStand === this.roh.syncStand &&
        s.stamm.version === this.roh.version
      ) {
        return false;
      }
    }
    return this.holeDelta(this.roh?.syncStand ?? 0, gen);
  }

  private async holeDelta(seit: number, gen: number): Promise<boolean> {
    const antwort = await this.opt.quelle.delta(seit, seit === 0 ? null : this.etag);
    this.pruefe(gen);
    if (antwort.status === 304) {
      return false;
    }
    // Älterer Server (z. B. nach einem Rückfall) kennt `seit` nicht und liefert den bisherigen
    // Stand ohne Cursor – dann wie bei 501 auf den Vollabruf umstellen (Bereitstellungsprüfung).
    if (
      typeof antwort.daten.vollstaendig !== 'boolean' ||
      typeof antwort.daten.syncStand !== 'number'
    ) {
      throw ohneDeltaFehler();
    }
    const angewandt = wendeAn(seit === 0 ? null : this.roh, antwort.daten);
    // Das Profil (P10) gehört zur Kasse, nicht zum Stamm – auch beim Vollstand behalten
    const neu =
      angewandt !== null && angewandt.profil === undefined && this.roh?.profil !== undefined
        ? { ...angewandt, profil: this.roh.profil }
        : angewandt;
    if (neu === null) {
      if (seit === 0) {
        throw new Error('Vollständiger Stand erwartet');
      }
      // Delta passt nicht zum lokalen Cursor (z. B. parallel geändert) → vollständig neu
      return this.holeDelta(0, gen);
    }
    const leer =
      !antwort.daten.vollstaendig &&
      antwort.daten.artikel.length === 0 &&
      antwort.daten.gruppen.length === 0 &&
      neu.version === this.roh?.version;
    if (leer) {
      this.etag = antwort.etag;
      return false;
    }
    // Zuerst dauerhaft ablegen, dann umschalten. Scheitert das Ablegen, gilt der neue Stand
    // trotzdem (neuer Preis ist sicherer als alter); der nächste Start lädt dann vollständig.
    await this.schreibeAblage(neu, gen);
    this.pruefe(gen);
    this.roh = neu;
    this.etag = antwort.etag;
    this.opt.uebernehme(neu);
    return true;
  }

  /** Vollabruf ohne Cursor (Server ohne Delta). Ändert sich die Version nicht: nichts tun. */
  private async holeVoll(gen: number): Promise<boolean> {
    const quelle = this.opt.quelle;
    if (quelle.voll === undefined) {
      throw new Error('Kein Vollabruf verfügbar');
    }
    const s = await quelle.voll();
    this.pruefe(gen);
    if (this.roh?.version === s.version && this.roh.syncStand === 0) {
      return false;
    }
    const neu: RohStamm = {
      unternehmerId: s.unternehmerId,
      version: s.version,
      aktualisiertAm: s.aktualisiertAm,
      syncStand: 0, // kein Cursor: nie Basis eines Deltas
      gruppen: s.gruppen,
      artikel: s.artikel,
      ...(this.roh?.profil === undefined ? {} : { profil: this.roh.profil }),
    };
    this.roh = neu;
    this.opt.uebernehme(neu);
    return true;
  }

  /** Profil laden; bei anderer ID/Version übernehmen (ablegen, dann anzeigen). */
  private async pruefeProfil(gen: number): Promise<boolean> {
    const quelle = this.opt.quelle;
    if (quelle.profil === undefined || this.roh === null) {
      return false;
    }
    const p = await quelle.profil();
    this.pruefe(gen);
    this.profilPruefen = false;
    const alt = this.roh.profil ?? null;
    if (alt?.id === p?.id && alt?.version === p?.version) {
      return false;
    }
    const neu: RohStamm = { ...this.roh, profil: p };
    await this.schreibeAblage(neu, gen);
    this.pruefe(gen);
    this.roh = neu;
    this.opt.uebernehme(neu);
    return true;
  }

  /**
   * Ablage nacheinander schreiben (Codex P10): Ein verspäteter Schreibvorgang einer gestoppten
   * Generation überschreibt nie einen neueren; noch nicht begonnene alte werden übersprungen.
   */
  private schreibKette: Promise<void> = Promise.resolve();
  private schreibeAblage(r: RohStamm, gen: number): Promise<void> {
    const lauf = this.schreibKette.then(async () => {
      if (gen !== this.generation) {
        return;
      }
      try {
        await this.opt.ablage.schreibe(r);
      } catch {
        // Arbeitsspeicher bleibt maßgeblich; der nächste Start lädt dann vollständig
      }
    });
    this.schreibKette = lauf;
    return lauf;
  }

  /** Prüfsumme gegen den Server; Abweichung bei gleichem Cursor → vollständig neu laden. */
  private async pruefeSumme(gen: number): Promise<void> {
    if (this.roh === null) {
      return;
    }
    const p = await this.opt.quelle.pruefsumme();
    this.pruefe(gen);
    this.letztePruefsumme = this.jetzt().getTime();
    if (p.syncStand !== this.roh.syncStand) {
      await this.holeDelta(this.roh.syncStand, gen);
      return;
    }
    const lokal = await sha256Hex(pruefsummenText(this.roh));
    this.pruefe(gen);
    if (p.sha256 !== lokal) {
      await this.holeDelta(0, gen);
    }
  }

  /** Ereigniskanal offen halten (mit Warten bei Fehlern); jeder Hinweis stößt den Abgleich an. */
  private oeffneKanal(): void {
    const quelle = this.opt.quelle;
    if (quelle.ereignisse === undefined || this.gestoppt || this.kanal !== null) {
      return;
    }
    const ab = new AbortController();
    this.kanal = ab;
    const gen = this.generation;
    void quelle
      .ereignisse((art, daten) => {
        if (gen !== this.generation) {
          return; // Kanal einer gestoppten Generation
        }
        this.kanalWarten = WARTEN_MIN_MS;
        if (art === 'hallo') {
          const start = (daten as { serverstart?: unknown } | null)?.serverstart;
          if (
            typeof start === 'string' &&
            this.serverstart !== null &&
            start !== this.serverstart
          ) {
            void this.anstossen('serverstart');
          }
          if (typeof start === 'string') {
            this.serverstart = start;
          }
        } else if (art === 'aenderung') {
          void this.anstossen('ereignis');
        }
      }, ab.signal)
      .catch((e: unknown) => {
        if (gen === this.generation && hatStatus(e) && e.status === 401) {
          this.setze({ art: 'anmeldungUngueltig' });
        }
      })
      .finally(() => {
        if (this.kanal === ab) {
          this.kanal = null;
        }
        if (
          gen !== this.generation ||
          this.gestoppt ||
          ab.signal.aborted ||
          this.zustandJetzt.art === 'anmeldungUngueltig'
        ) {
          return;
        }
        // Server schloss (10-min-Grenze) oder Netz weg: nach kurzer Pause neu verbinden
        const warten = this.kanalWarten;
        this.kanalWarten = Math.min(WARTEN_MAX_MS, this.kanalWarten * 2);
        this.planer.setTimeout(() => {
          this.oeffneKanal();
        }, warten);
      });
  }

  /** Nach neuer Einrichtung/Anmeldung oder Wiederverbindung: Kanal wieder öffnen. */
  wiederverbinden(): void {
    this.kanalWarten = WARTEN_MIN_MS;
    this.oeffneKanal();
    void this.anstossen('anmeldung');
  }
}
